import React, { useEffect, useState } from 'react';
import {
    Input,
    Form,
    Select,
    Modal,
    Tabs,
    Row,
    Checkbox,
    Col,
    Upload,
    InputNumber,
    Space,
    Image,
    Button,
    Tooltip,
    notification,
    Spin,
} from 'antd';
import { Operator, allOperatorTypes } from '../../../shared/types';
import { handleFormChange, validateMessages } from '../../../shared/Utils';
import { useGetAllCityQuery } from '../../../GraphQL/hooks/useGetAllCityQuery';
import { useCurrentOperator } from '../hooks/useCurrentOperator';
import { useUpdateOperatorMutation } from '../../../GraphQL/hooks/useUpdateOperatorMutation';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import ImageUploadPreview from '../../../shared/Components/ImageUploadPreview';

const OperatorEditModal = ({ isOpen, onRequestClose, updateList }) => {
    const [form] = Form.useForm();
    const [formError, setFormError] = useState(false);
    const { currentOperator } = useCurrentOperator();
    const [prefix, setPrefix] = useState<string>('+216');
    const { data: cities } = useGetAllCityQuery();
    const { mutation, loading: creating } = useUpdateOperatorMutation();

    const [activeTab, setActiveTab] = useState('operator-data');
    const [fileUpload, setFileUpload] = useState<any>();

    const validatePhoneNumber = (_, value) => {
        const phoneNumberRegex = /^[0-9]{8,9}$/;
        if (!value || phoneNumberRegex.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Invalid phone number format!'));
    };

    const prefixSelector = (
        <Form.Item noStyle>
            <Select style={{ width: 80 }} value={prefix} defaultValue={'+216'} onChange={(prefix) => setPrefix(prefix)}>
                <Select.Option value="+216">+216</Select.Option>
                <Select.Option value="+218">+218</Select.Option>
                <Select.Option value="+213">+213</Select.Option>
                <Select.Option value="+212">+212</Select.Option>
            </Select>
        </Form.Item>
    );

    const handleCityChange = (value: string) => {
        const selectedCity = cities?.find((city) => city.cityName === value);
        if (selectedCity) {
            form.setFieldsValue({
                address: {
                    governorate: selectedCity.governorate,
                    country: selectedCity.country,
                },
            });
        }
    };

    useEffect(() => {
        let countryCode: string = '';
        let phoneNumber: string = '';
        const phone: string = currentOperator?.phone;
        if (phone) {
            countryCode = phone.substring(0, 4);
            phoneNumber = phone.substring(4, phone.length);
            setPrefix(countryCode);
        }
        setFileUpload(currentOperator?.logo);
        form.setFieldsValue({ ...currentOperator, phone: phoneNumber });
    }, [form, currentOperator]);

    const handleCommit = async () => {
        try {
            const values = await form.validateFields();
            const { logo, ...valuesWithoutLogo } = values;

            if (values) {
                mutation(
                    {
                        ...valuesWithoutLogo,
                        _id: currentOperator._id,
                        phone: prefix + values?.phone,
                        settingId: {
                            ...values.settingId,
                            _id: currentOperator?.settingId._id,
                        },
                    },
                    fileUpload
                )
                    .then((res) => {
                        onRequestClose(false);
                        updateList();
                    })
                    .finally(() => {
                        notification.success({
                            message: 'Operator updated successfully',
                            description: 'The operator has been updated successfully.',
                        });
                    })
                    .catch((err) => {
                        notification.error({
                            message: 'Failed to update operator',
                            description: 'An error occurred while updating the operator.',
                        });

                        console.error('Failed to update operator:', err);
                    });
            } else {
                notification.error({
                    message: 'Form validation failed',
                    description: 'Please fill in the required fields.',
                });
            }
        } catch (errorInfo) {
            notification.error({
                message: 'Form validation failed',
                description: 'Please check the form for errors and try again.',
            });
            console.error('Form validation failed:', errorInfo);
        }
    };

    const handleCancel = () => {
        onRequestClose(false);
    };

    const tabItems = [
        {
            key: 'operator-data',
            label: 'Business Info',
            children: (
                <Row gutter={[0, 8]}>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[{ required: true }]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input type="text" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="phone"
                            label="Phone"
                            rules={[
                                { required: true, message: 'Please enter your phone number!' },
                                { validator: validatePhoneNumber },
                            ]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input type="tel" addonBefore={prefixSelector} placeholder={'72 234 567'} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { type: 'email', message: 'Please provide a valid email!' },
                                { required: true, message: 'Please enter your E-mail!' },
                            ]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input type="email" placeholder="exemple@mail.com" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="website"
                            label="Website"
                            rules={[
                                { required: false },
                                { type: 'url', message: 'Please provide a valid website URL!' },
                            ]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input type="url" placeholder="www.exemple.com" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="type"
                            label="Type"
                            rules={[{ required: true }]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Select placeholder="Select type of operators">
                                {(allOperatorTypes ?? []).map((type) => (
                                    <Select.Option key={type} value={type}>
                                        {type}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="tva"
                            label="Fiscal N°"
                            rules={[{ required: false }]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input
                                type="text"
                                suffix={
                                    <Tooltip title="This is your Tax ID Number" placement="rightTop">
                                        <InfoCircleOutlined style={{ color: '#1677ff', fontSize: '20px' }} />
                                    </Tooltip>
                                }
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Address"
                            style={{ display: 'inline-block', width: '90%', marginBottom: '0px' }}
                        >
                            <Space.Compact>
                                <Form.Item
                                    name={['address', 'street']}
                                    rules={[{ required: false }]}
                                    style={{ width: '60%' }}
                                >
                                    <Input type="text" placeholder="Street name" />
                                </Form.Item>
                                <Form.Item
                                    name={['address', 'postalCode']}
                                    style={{ width: '40%' }}
                                    rules={[{ required: false }]}
                                >
                                    <InputNumber type="number" min={1} max={99999} placeholder="Postal code" />
                                </Form.Item>
                            </Space.Compact>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name={['address', 'city']}
                            label="City"
                            style={{ display: 'inline-block', width: '90%' }}
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select City" style={{ width: '90%' }} onChange={handleCityChange}>
                                {(cities ?? []).map((city) => (
                                    <Select.Option key={city._id} value={city.cityName}>
                                        {city.cityName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name={['address', 'governorate']}
                            label="Governorate"
                            style={{ display: 'inline-block', width: '90%' }}
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select Governorate" disabled />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name={['address', 'country']}
                            label="Country"
                            style={{ display: 'inline-block', width: '90%' }}
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select country" disabled />
                        </Form.Item>
                    </Col>
                    <Col span={5} offset={2}>
                        <Form.Item
                            name={'logo'}
                            label="Logo"
                            valuePropName={'logo'}
                            getValueFromEvent={(event) => event?.file}
                            rules={[
                                { required: false, message: 'please upload your Logo.' },
                                {
                                    validator(_, fileList) {
                                        return new Promise((resolve, reject) => {
                                            if (fileList?.size > 5000000) {
                                                reject('File size exceeded');
                                            } else {
                                                resolve('Successs');
                                            }
                                        });
                                    },
                                },
                            ]}
                            style={{ display: 'inline-block' }}
                        >
                            <Upload
                                accept="image/png, image/jpeg"
                                maxCount={1}
                                beforeUpload={() => false}
                                onChange={({ file }) => setFileUpload(file)}
                                showUploadList={false}
                                listType="picture-card"
                                openFileDialogOnClick={!fileUpload}
                            >
                                {fileUpload?.size > 0 ? (
                                    <ImageUploadPreview file={fileUpload} onDelete={() => setFileUpload(null)} />
                                ) : (
                                    <div style={{ textAlign: 'center' }}>
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>Upload Image</div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>
                    </Col>

                    <Col span={17}>
                        <Form.Item
                            name="description"
                            label="Description"
                            rules={[{ required: false }]}
                            style={{ display: 'inline-block', width: '93%' }}
                        >
                            <Input.TextArea showCount maxLength={100} rows={4} />
                        </Form.Item>
                    </Col>
                </Row>
            ),
        },
        {
            key: 'settings-data',
            label: 'Settings',
            children: (
                <Row gutter={[0, 8]}>
                    <Col span={12}>
                        <Form.Item
                            name={['settingId', 'fullname']}
                            label="Fullname"
                            rules={[{ required: false }]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input type="text" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name={['settingId', 'email']}
                            label="Email"
                            rules={[
                                { type: 'email', message: 'Please provide a valid email!' },
                                { required: true, message: 'Please enter your E-mail!' },
                            ]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input type="email" autoComplete="new-email" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name={['settingId', 'password']}
                            label="Password"
                            rules={[{ required: false }]}
                            style={{ display: 'inline-block', width: '90%' }}
                        >
                            <Input.Password autoComplete="new-password" />
                        </Form.Item>
                    </Col>

                    <Col span={11}>
                        <Form.Item
                            label="Roles"
                            name={['settingId', 'sectionRoles']}
                            rules={[{ required: false }]}
                            style={{ display: 'inline-block', width: '100%' }}
                        >
                            <Checkbox.Group>
                                <Row gutter={[32, 8]}>
                                    <Col span={10}>
                                        <Checkbox value="Reports">Reports</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Map">Map</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="MapTaxi">MapTaxi</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Fleet">Fleet</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Riders">Riders</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="TripsMicro">Trips Micro</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="TripsTaxi">Trips Taxi</Checkbox>
                                    </Col>
                                    <Col span={8}>
                                        <Checkbox value="Promos">Promos</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Operators">Operators</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Bookings">Bookings</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Settings">Settings</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Pricing">Pricings</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Passes">Passes</Checkbox>
                                    </Col>
                                    <Col span={10}>
                                        <Checkbox value="Support">Support</Checkbox>
                                    </Col>
                                    <Col span={16}>
                                        <Checkbox value="Campaign">Campaign</Checkbox>
                                    </Col>
                                    <Col span={16}>
                                        <Checkbox value="TaxiDrivers">Taxi Drivers</Checkbox>
                                    </Col>
                                    <Col span={16}>
                                        <Checkbox value="FinancialOverview">Financial Overview</Checkbox>
                                    </Col>
                                    <Col span={16}>
                                        <Checkbox value="FleetProblem">Fleet Problem</Checkbox>
                                    </Col>
                                    <Col span={16}>
                                        <Checkbox value="SuperOperator">Super Operator</Checkbox>
                                    </Col>
                                </Row>
                            </Checkbox.Group>
                        </Form.Item>
                    </Col>
                </Row>
            ),
        },
    ];

    return (
        <Modal
            forceRender
            title="Edit operator"
            width={750}
            open={isOpen}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="edit" type="primary" onClick={handleCommit} disabled={formError}>
                    Edit
                </Button>,
            ]}
        >
            <Spin spinning={creating}>
                <Form<Operator>
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 24 }}
                    form={form}
                    autoComplete="off"
                    name="nest-messages"
                    onFieldsChange={() => {
                        setFormError(handleFormChange(form));
                    }}
                    validateMessages={validateMessages}
                >
                    <Tabs defaultActiveKey={activeTab} items={tabItems} />
                </Form>
            </Spin>
        </Modal>
    );
};
export default OperatorEditModal;
